import { Coordinate, getCurrentTime } from "../models"
import type { TileMapMetaProvider } from "../tile_map_meta_provider"
import type { TileInteraction } from "../entity_tile_interactions"

export const HATCH_SWING_MS = 550
export const HATCH_BREAK_MS = 850
// Sixteen strikes buy at least 18 seconds against one attacker, even with an immediate first hit.
export const HATCH_MAX_HP = 16
export const HATCH_STRIKE_MS = 1200
export const HATCH_REPAIR_QUIET_MS = 3000

/** Installed on the upper opening; mirrored onto both ends for presentation and passage. */
export interface HatchView {
    open: boolean
    fromOpen?: boolean
    changedAt?: number
    /** Missing health on older saves means an undamaged hatch. */
    hp?: number
    lastHitAt?: number
}

export const hatchHealth = (hatch: HatchView): number => hatch.hp ?? HATCH_MAX_HP

export const hatchAt = (map: Pick<TileMapMetaProvider, "getMapDeltaAt">, at: Coordinate): HatchView | undefined => {
    const delta = map.getMapDeltaAt(at.x, at.y, "sprite2")
    return !delta?.removed ? delta?.context?.hatch : undefined
}

export const hatchMoving = (hatch: HatchView, now = getCurrentTime()): boolean =>
    hatch.changedAt !== undefined && hatch.fromOpen !== hatch.open && now < hatch.changedAt + HATCH_SWING_MS

/** Keep passage shut throughout either swing. Opening becomes usable only at full extension. */
export const hatchBlocksPassage = (map: Pick<TileMapMetaProvider, "getMapDeltaAt">, at: Coordinate,
    now = getCurrentTime()): boolean => {
    const hatch = hatchAt(map, at)
    return !!hatch && (!hatch.open || hatchMoving(hatch, now))
}

/** Hinge angle fraction, with a gentle acceleration and a soft landing. */
export const hatchOpenness = (hatch: HatchView, now = getCurrentTime()): number => {
    if (!hatchMoving(hatch, now)) return hatch.open ? 1 : 0
    const t = Math.max(0, Math.min(1, (now - hatch.changedAt) / HATCH_SWING_MS))
    const ease = t * t * (3 - 2 * t)
    return hatch.open ? ease : 1 - ease
}

export const hatchInteractions = (direction: "up" | "down", hatch?: HatchView): TileInteraction[] => [
    { name: direction === "up" ? "ascend" : "descend", displayName: direction === "up" ? "Take stairs up" : "Take stairs down" },
    ...(hatch ? [{ name: hatch.open ? "close-hatch" : "open-hatch", displayName: hatch.open ? "Close hatch" : "Open hatch" }]
        : [{ name: "install-hatch", displayName: "Install wooden hatch" }]),
    ...(hatch && hatchHealth(hatch) < HATCH_MAX_HP ? [{ name: "repair-hatch", displayName: "Repair hatch (1 plank)" }]
        : hatch ? [{ name: "remove-hatch", displayName: "Remove hatch" }] : []),
    { name: "dismantle", displayName: "Dismantle" },
]
